import { useMemo, useRef, useState } from 'react'
import { Modal } from '../ui/Modal'
import { Input } from '../ui/Input'
import { GatedButton } from '../ui/GatedButton'
import { Button } from '../ui/Button'
import { EmptyState } from '../ui/EmptyState'
import { useToast } from '../../contexts/ToastContext'
import { groups } from '../../lib/api'
import { useMyComposableCircles, useInvalidateGroups } from '../../hooks/useGroups'
import { useActionBoundary } from '../../hooks/useActionBoundary'
import { usePerson } from '../../contexts/PersonContext'
import { cn } from '../../lib/cn'
import type { GroupSummaryDto, CircleSummaryDto } from '../../types/community'

const GAMES = [
  { slug: 'minecraft', label: 'Minecraft' },
  { slug: 'wobbly-life', label: 'Wobbly Life' },
  { slug: 'mecha-chameleon', label: 'Mecha Chameleon' },
  { slug: 'other', label: 'Something else' },
]

interface NewGroupModalProps {
  open: boolean
  onClose: () => void
  mode: 'create' | 'join'
  /** Join mode: the group being joined (from browse). */
  group?: { id: string; name: string }
  onCreated?: (summary: GroupSummaryDto) => void
  onJoined?: () => void
}

/**
 * Create + join a group (DESIGN §3.15, COTC_GroupsModelRevision). You join a group as YOURSELF;
 * bringing a circle invites its members (fan-out) — it never enrols them. Both modes share the
 * own-circle multi-select; join mode hides the name + game.
 */
export function NewGroupModal({ open, onClose, mode, group, onCreated, onJoined }: NewGroupModalProps) {
  const { flash } = useToast()
  const { role } = usePerson()
  const capture = useActionBoundary(group?.id ?? mode)
  const inFlight = useRef(false)
  const invalidate = useInvalidateGroups()
  const { data: circles = [], isLoading, isError, refetch } = useMyComposableCircles(open)

  const [name, setName] = useState('')
  const [gameSlug, setGameSlug] = useState('minecraft')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [visibility, setVisibility] = useState<'Public' | 'Private'>('Public')
  const [audience, setAudience] = useState<'Everyone' | 'KidsOnly' | 'AdultsOnly'>('Everyone')
  const [access, setAccess] = useState<'Open' | 'VouchNeeded'>('Open')

  const hasNoCircles = !isLoading && circles.length === 0

  function toggleCircle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const gated = useMemo(() => {
    if (busy) return true
    if (mode === 'create' && visibility === 'Private' && selected.size < 1) return true
    if (role !== 'parent') return true
    if (mode === 'create' && name.trim().length < 2) return true
    return false
  }, [busy, selected.size, mode, name, visibility, role])

  const gateHint = role !== 'parent' ? 'A guardian starts or joins groups here.' :
    mode === 'create' && name.trim().length < 2 ? 'Give your group a name of at least two characters.' :
    'A private group needs at least one of your circles.'

  function reset() {
    setName('')
    setGameSlug('minecraft')
    setSelected(new Set())
    setBusy(false)
  }

  async function submit() {
    if (inFlight.current || gated) return
    const current = capture()
    inFlight.current = true
    setBusy(true); setError(null)
    const circleIds = [...selected]
    try {
      if (mode === 'create') {
        const summary = await groups.createGroup({ name: name.trim(), gameSlug, circleIds, visibility, audience, access })
        if (!current()) return
        flash(`${summary.name} created — you host it.`)
        invalidate(summary.id)
        onCreated?.(summary)
      } else if (group) {
        await groups.joinGroup(group.id, circleIds)
        if (!current()) return
        flash(`Join submitted for ${group.name}. Check your membership in the group.`)
        invalidate(group.id)
        onJoined?.()
      }
      reset()
      onClose()
    } catch (err) {
      if (current()) { setError((err as Error)?.message ?? 'Something went wrong. Please try again.'); setBusy(false) }
    } finally { inFlight.current = false }
  }

  const title = mode === 'join' ? `Join ${group?.name ?? 'a group'}` : 'Start a group'

  return (
    <Modal open={open} onClose={() => { if (!busy) onClose() }} title={title}>
      {mode === 'join' ? (
        <p className="mb-5 text-[14px] leading-relaxed text-ink-muted">
          You join a group as yourself. Optionally pick circles so people you already trust can
          find you here — bringing a circle invites its members, it never adds them for you.
        </p>
      ) : (
        <>
          <Input
            label="Group name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Weeknight Builders"
            className="mb-5"
          />
          <div className="mb-5 grid gap-3 sm:grid-cols-3">
            <label className="text-sm">Visibility<select aria-label="Visibility" className="mt-1 block w-full rounded-input border border-line bg-paper-surface p-2" value={visibility} onChange={(e) => setVisibility(e.target.value as typeof visibility)}><option>Public</option><option>Private</option></select></label>
            <label className="text-sm">Players<select aria-label="Players" className="mt-1 block w-full rounded-input border border-line bg-paper-surface p-2" value={audience} onChange={(e) => setAudience(e.target.value as typeof audience)}><option value="Everyone">Everyone</option><option value="KidsOnly">Kids only</option><option value="AdultsOnly">Adults only</option></select></label>
            <label className="text-sm">Joining<select aria-label="Joining" className="mt-1 block w-full rounded-input border border-line bg-paper-surface p-2" value={access} onChange={(e) => setAccess(e.target.value as typeof access)}><option value="Open">Open</option><option value="VouchNeeded">Host approval</option></select></label>
          </div>
          <div className="mb-5">
            <span className="mb-[7px] block font-mono text-[11px] uppercase tracking-label text-ink-faint">
              Game
            </span>
            <div className="flex flex-wrap gap-2">
              {GAMES.map((g) => (
                <button
                  key={g.slug}
                  type="button"
                  aria-pressed={gameSlug === g.slug} onClick={() => setGameSlug(g.slug)}
                  className={cn(
                    'rounded-pill border px-[14px] py-2 font-sans text-[13px] transition-colors',
                    gameSlug === g.slug
                      ? 'border-terra bg-terra text-paper-surface'
                      : 'border-line bg-paper-surface text-ink-muted hover:border-terra',
                  )}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      <div className="mb-6">
        <span className="mb-[7px] block font-mono text-[11px] uppercase tracking-label text-ink-faint">
          {mode === 'join' ? 'Circles to bring in' : 'Circles in this group'}
        </span>
        {isError ? <p role="alert">Your circles could not load. You can continue without one for a public group. <button onClick={() => void refetch()}>Retry circles</button></p> : isLoading ? (
          <p className="text-[13px] text-ink-muted">Loading your circles…</p>
        ) : hasNoCircles ? (
          <EmptyState
            dashed
            description="No circles yet. Public groups can start without one; circle invitations can be added later."
          />
        ) : (
          <div className="flex flex-col gap-2">
            {circles.map((c: CircleSummaryDto) => {
              const on = selected.has(c.id)
              return (
                <label
                  key={c.id}
                  className={cn(
                    'flex cursor-pointer items-center gap-3 rounded-input border px-[14px] py-3 transition-colors',
                    on ? 'border-terra bg-paper-panel' : 'border-line bg-paper-surface hover:border-terra',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => toggleCircle(c.id)}
                    className="h-4 w-4 accent-terra"
                  />
                  <span className="h-[10px] w-[10px] flex-none rounded-full" style={{ background: c.dot }} />
                  <span className="flex-1 text-[14px] text-ink">{c.name}</span>
                  <span className="font-mono text-[10px] uppercase tracking-label text-ink-faint">{c.type}</span>
                </label>
              )
            })}
          </div>
        )}
      </div>

      {error && <p role="alert" className="mb-3 text-sm text-honey-fg">{error}</p>}
      <div className="flex items-center justify-end gap-3">
        <Button variant="outline" size="sm" disabled={busy} onClick={() => { if (!inFlight.current) onClose() }}>
          Cancel
        </Button>
        <GatedButton gated={gated} disabled={busy} hint={gateHint} variant="accent" onClick={submit}>
          {mode === 'join' ? 'Join group' : 'Start group'}
        </GatedButton>
      </div>
    </Modal>
  )
}
